import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { activityForReview, participantName } from '../../utils/developmentDemo';
export function ReviewerPlans() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const eligible = state.reviews.filter(review => {
    const submission = state.submissions.find(item => item.id === review.submissionId);
    return review.outcome === 'reviewed' && submission?.round === 'initial' && !activityForReview(state, review.id);
  });
  const assign = (reviewId: string) => {
    const draft = state.ui.activityDrafts[reviewId] ?? config.suggestedActivity;
    if (!draft.title.trim() || !draft.instructions.trim()) {
      setErrors(current => ({
        ...current,
        [reviewId]: 'An activity needs a title and practice instructions.'
      }));
      return;
    }
    setErrors(current => ({
      ...current,
      [reviewId]: ''
    }));
    dispatch({
      type: 'assignActivity',
      reviewId
    });
  };
  return <div data-id="element-913" data-mp="2jspfh5qj8aa5">
      <DemoSectionHeader title="Development Plans" description="Turn a completed review into a focused practice activity. Each activity stays linked to the review it came from." data-id="element-914" data-mp="2opfv5ryfd3v1" />

      <section aria-labelledby="ready-to-assign" data-id="element-915" data-mp="3ch39zbiewogy">
        <h2 id="ready-to-assign" className="font-heading text-lg font-bold text-ink-dark" data-id="element-916" data-mp="3hd1v8q8g1qd5">
          Reviews ready for an activity ({eligible.length})
        </h2>
        {eligible.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-917" data-mp="37n8ktsk1fhah">No completed initial reviews are waiting for an activity.</p> : <ul className="mt-4 space-y-5" data-id="element-918" data-mp="3vcjopiwoprsu">
            {eligible.map(review => {
          const draft = state.ui.activityDrafts[review.id] ?? config.suggestedActivity;
          const setDraft = (patch: Partial<typeof draft>) => dispatch({
            type: 'setActivityDraft',
            reviewId: review.id,
            draft: {
              ...draft,
              ...patch
            }
          });
          return <li key={review.id} data-id="element-919" data-mp="4tfzjckd9x04">
                  <article className={`${glassPanel} p-6`} data-id="element-920" data-mp="4wlmom9fqot7">
                    <p className="text-xs text-ink-muted" data-id="element-921" data-mp="3vzqv4t6ly5k2">
                      Originating review <span className="font-mono font-semibold" data-id="element-922" data-mp="3r28vxuie74p9">{review.id}</span> · {review.submissionId} · {formatTimestamp(review.at)}
                    </p>
                    <h3 className="mt-1 font-heading text-xl font-bold text-ink-dark" data-id="element-923" data-mp="3m8w6xj9swvvz">{participantName(config, review.participantId)}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-924" data-mp="3dau7v99modbc">Feedback: {review.feedback}</p>
                    <div className="mt-5 grid grid-cols-1 gap-4" data-id="element-925" data-mp="38bhd82cim1y7">
                      <div data-id="element-926" data-mp="33c4p7e9wqou1">
                        <label htmlFor={`${review.id}-title`} className={labelClass} data-id="element-927" data-mp="33buzzswoslpe">
                          Activity title
                        </label>
                        <input id={`${review.id}-title`} value={draft.title} onChange={event => setDraft({
                    title: event.target.value
                  })} className={fieldClass} data-id="element-928" data-mp="1iwgctj3hba9i" />
                      </div>
                      <div data-id="element-929" data-mp="1sq5kah7kbypf">
                        <label htmlFor={`${review.id}-instructions`} className={labelClass} data-id="element-930" data-mp="1wtlgjcfpzx1l">
                          Practice instructions
                        </label>
                        <textarea id={`${review.id}-instructions`} rows={3} value={draft.instructions} onChange={event => setDraft({
                    instructions: event.target.value
                  })} className={fieldClass} data-id="element-931" data-mp="101rmo2kdz4ee" />
                      </div>
                    </div>
                    {errors[review.id] && <p className={errorClass} data-id="element-932" data-mp="legdu8fl9yhz">{errors[review.id]}</p>}
                    <button type="button" onClick={() => assign(review.id)} className={`mt-5 ${primaryButton}`} data-id="element-933" data-mp="lcltav88c8ef">
                      Assign activity
                    </button>
                  </article>
                </li>;
        })}
          </ul>}
      </section>

      <section aria-labelledby="assigned-activities" className="mt-10" data-id="element-934" data-mp="c1zdn68eg4vs">
        <h2 id="assigned-activities" className="font-heading text-lg font-bold text-ink-dark" data-id="element-935" data-mp="78ef9d2w98w2">
          Assigned activities ({state.activities.length})
        </h2>
        {state.activities.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-936" data-mp="g0yjqu5va7ot">None yet.</p> : <div className={`${glassPanel} mt-3 overflow-x-auto`} data-id="element-937" data-mp="uxfqt970x05k">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-938" data-mp="pzxh3gl6mgwi">
              <thead data-id="element-939" data-mp="pxtdkzr73jwr">
                <tr className="border-b border-line text-xs text-ink-muted" data-id="element-940" data-mp="2wpu4fw2jbig6">
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-941" data-mp="2wryjna5vw3vr">Activity</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-942" data-mp="2rsvc9j2fllkm">{config.participantNoun}</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-943" data-mp="179ru5bo7yabc">Status</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-944" data-mp="1c6rud5ye0iuj">Reflection</th>
                </tr>
              </thead>
              <tbody data-id="element-945" data-mp="xh2mkrvshgla">
                {state.activities.map(activity => <tr key={activity.id} className="border-b border-line align-top last:border-b-0" data-id="element-946" data-mp="12gp1d5r9n4w7">
                    <td className="px-4 py-3" data-id="element-947" data-mp="ogxiaqlw6ybc">
                      <span className="font-semibold text-ink-dark" data-id="element-948" data-mp="sju5yvqahkm5">{activity.title}</span>
                      <span className="block text-xs text-ink-muted" data-id="element-949" data-mp="ekaz0697sfbf">
                        {activity.id} · from {activity.reviewId}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-dark" data-id="element-950" data-mp="jl8lgv19bviq">{participantName(config, activity.participantId)}</td>
                    <td className="px-4 py-3 font-semibold text-ink-dark whitespace-nowrap" data-id="element-951" data-mp="2dx6nabx8lp4l">{activity.status === 'completed' ? 'Completed' : 'In Progress'}</td>
                    <td className="px-4 py-3 whitespace-pre-wrap text-ink-muted" data-id="element-952" data-mp="2chkr8vaiwhre">{activity.reflection || '—'}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </section>
    </div>;
}